'use client';

import React from 'react';

import { Box, BoxProps } from './box';
import { HStack } from './stack';
import { Tabs } from './tabs';

export interface VerticalTabItem {
  value: string;
  label: string;
  icon?: React.ReactNode;
  content: React.ReactNode;
  disabled?: boolean;
}

export interface VerticalTabsProps extends BoxProps {
  items: VerticalTabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (value: string) => void;
  tabWidth?: string | number;
  variant?: 'line' | 'enclosed' | 'soft-rounded';
  size?: 'sm' | 'md' | 'lg';
}

/**
 * VerticalTabs component that provides a vertical tab layout
 * Built on top of the compound Tabs system with predefined layout
 */
export const VerticalTabs: React.FC<VerticalTabsProps> = ({
  items,
  defaultValue,
  value,
  onValueChange,
  tabWidth = '200px',
  variant = 'line',
  size = 'md',
  ...props
}) => {
  const sizeConfig = {
    sm: {
      padding: 2,
      fontSize: 'sm',
      gap: 1,
    },
    md: {
      padding: 3,
      fontSize: 'md',
      gap: 2,
    },
    lg: {
      padding: 4,
      fontSize: 'lg',
      gap: 3,
    },
  };

  const config = sizeConfig[size];

  return (
    <Box {...props}>
      <Tabs.Root
        defaultValue={defaultValue || items[0]?.value}
        value={value}
        onValueChange={onValueChange}
        variant={variant}
        orientation="vertical"
      >
        <HStack align="flex-start" gap={6}>
          {/* Tab List */}
          <Box width={tabWidth} flexShrink={0}>
            <Tabs.List>
              {items.map((item) => (
                <Tabs.Trigger
                  key={item.value}
                  value={item.value}
                  disabled={item.disabled}
                  padding={config.padding}
                  fontSize={config.fontSize}
                  width="100%"
                  justifyContent="flex-start"
                  gap={config.gap}
                >
                  {item.icon && <Box flexShrink={0}>{item.icon}</Box>}
                  <Box textAlign="left">{item.label}</Box>
                </Tabs.Trigger>
              ))}
            </Tabs.List>
          </Box>

          {/* Tab Contents */}
          <Box flex={1}>
            <Tabs.ContentGroup>
              {items.map((item) => (
                <Tabs.Content key={item.value} value={item.value}>
                  {item.content}
                </Tabs.Content>
              ))}
            </Tabs.ContentGroup>
          </Box>
        </HStack>
      </Tabs.Root>
    </Box>
  );
};

export default VerticalTabs;
